import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  qualityAPI,
  Concession,
  ConcessionComment,
  ConcessionActionLog,
} from '../../lib/api';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useIsCustomer, useIsSupplier } from '../../hooks/usePortalMode';
import { DialogState, ApprovalFormState } from './types';

export function useConcessionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCustomer = useIsCustomer();
  const isSupplier = useIsSupplier();

  const [concession, setConcession] = useState<Concession | null>(null);
  const [comments, setComments] = useState<ConcessionComment[]>([]);
  const [actionLogs, setActionLogs] = useState<ConcessionActionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog states
  const [dialogs, setDialogs] = useState<DialogState>({
    approve: false,
    reject: false,
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalForm, setApprovalForm] = useState<ApprovalFormState>({
    notes: '',
    quantityApproved: '',
    validUntil: '',
  });

  // Comment state
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const [concessionRes, commentsRes, logsRes] = await Promise.all([
        qualityAPI.getConcession(parseInt(id)),
        qualityAPI.getConcessionComments(parseInt(id)),
        qualityAPI.getConcessionActionLog(parseInt(id)),
      ]);

      setConcession(concessionRes.data);
      setComments(commentsRes.data);
      setActionLogs(logsRes.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load concession'));
    } finally {
      setLoading(false);
    }
  }, [id, isCustomer]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async () => {
    if (!concession) return;
    try {
      setActionLoading(true);
      await qualityAPI.submitConcession(concession.id);
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to submit concession'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkUnderReview = async () => {
    if (!concession) return;
    try {
      setActionLoading(true);
      await qualityAPI.markConcessionUnderReview(concession.id);
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to mark under review'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!concession) return;
    try {
      setActionLoading(true);
      await qualityAPI.approveConcession(concession.id, {
        review_notes: approvalForm.notes,
        quantity_approved: approvalForm.quantityApproved ? parseInt(approvalForm.quantityApproved) : undefined,
        valid_until: approvalForm.validUntil || undefined,
      });
      setDialogs(prev => ({ ...prev, approve: false }));
      setApprovalForm({ notes: '', quantityApproved: '', validUntil: '' });
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to approve concession'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!concession || !rejectionReason) return;
    try {
      setActionLoading(true);
      await qualityAPI.rejectConcession(concession.id, rejectionReason);
      setDialogs(prev => ({ ...prev, reject: false }));
      setRejectionReason('');
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to reject concession'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = async () => {
    if (!concession) return;
    try {
      setActionLoading(true);
      await qualityAPI.closeConcession(concession.id);
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to close concession'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptRisk = async () => {
    if (!concession) return;
    try {
      setActionLoading(true);
      await qualityAPI.acceptConcessionRisk(concession.id);
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to accept risk'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!concession || !newComment.trim()) return;
    try {
      setCommentLoading(true);
      await qualityAPI.addConcessionComment(concession.id, newComment, isInternalComment);
      setNewComment('');
      setIsInternalComment(false);
      // Refresh comments
      const commentsRes = await qualityAPI.getConcessionComments(concession.id);
      setComments(commentsRes.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to add comment'));
    } finally {
      setCommentLoading(false);
    }
  };

  return {
    // Navigation
    navigate,

    // User info
    isCustomer,
    isSupplier,

    // Loading states
    loading,
    error,
    actionLoading,
    commentLoading,

    // Data
    concession,
    comments,
    actionLogs,

    // Dialog state
    dialogs,
    setDialogs,
    rejectionReason,
    setRejectionReason,
    approvalForm,
    setApprovalForm,

    // Comment state
    newComment,
    setNewComment,
    isInternalComment,
    setIsInternalComment,

    // Actions
    handleSubmit,
    handleMarkUnderReview,
    handleApprove,
    handleReject,
    handleClose,
    handleAcceptRisk,
    handleAddComment,
  };
}
